import { useDeleteChat } from '@repo/api/chat/useDeleteChat';
import { cn } from '@repo/lib/cn';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { CheckCircle2Icon, MessageCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the DeleteChat component.
 * @property {number} id - The chat ID to delete.
 * @property {string} [name] - The name of the chat.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface DeleteChatProps {
  id: number;
  name?: string;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function DeleteChat(deleteChatProps: DeleteChatProps) {
  const { id, name, open, onOpenChange } = deleteChatProps;
  const navigate = useNavigate();

  const { mutateAsync, isPending, isSuccess } = useDeleteChat();

  const onSubmit = async () => {
    navigate('/chat');
    await mutateAsync(id).catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <MessageCircleIcon />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Czy chcesz usunąć ten chat?</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Usuń wybranego chata</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'p-4'}>
            <p className={'text-text-secondary text-sm'}>
              Czy na pewno chcesz usunąć czat: <b> {name} ?</b>
            </p>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'}>Zamknij</AlertDialogCancel>
          <AlertDialogAction className={cn(isSuccess && 'hidden')} disabled={isPending} onClick={onSubmit}>
            <CheckCircle2Icon />
            Potwierdź
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
